import { RootState } from "@/store"
import { AppPreferenceCache } from "@/utils/storage"
import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { AppSelectOption } from "@/widgets/Common"
import { Resolution } from "@/sdk"
import { AutoLogoutTimeGap } from "@/api/model"

interface VideoState {
  // 是否处于全屏
  isFullScreen: boolean
  // 是否弹出退出弹窗
  isOpen: boolean
  // 鼠标滚轮方向跟随 Mac 使用习惯
  isUseMacMouseWheelDir: boolean
  // 鼠标滚轮速度比例
  mouseScrollSpeed: number
  // 复制粘贴（和常见快捷键）跟随 Mac 使用习惯
  isUseCommonShortcut: boolean
  // 游戏模式开关
  isGameModel: boolean
  // 是否锁定鼠标(游戏模式下)
  isThreeDMouse: boolean
  // 分辨率列表
  resolutionList: AppSelectOption[]
  // 用户选择的分辨率
  resolutionItem: Resolution
  // Agent 上报的主显示器分辨率
  agentResolutionItem: AppSelectOption | null
  // 用户多久未操作云电脑自动退出 的时间间隔
  autoLogoutTimeGap: AutoLogoutTimeGap
}

const {
  isUseMacMouseWheelDir = true,
  mouseScrollSpeed = 0.25,
  isUseCommonShortcut = true,
  isGameModel = false,
  resolutionItem,
  autoLogoutTimeGap = "thirty_minutes",
} = AppPreferenceCache.getAppPreference() ?? {}

const initialState: VideoState = {
  isFullScreen: false,
  isOpen: false,
  isUseMacMouseWheelDir,
  mouseScrollSpeed,
  isUseCommonShortcut,
  isGameModel,
  isThreeDMouse: false,
  resolutionList: [],
  resolutionItem: resolutionItem ?? { width: 0, height: 0 },
  agentResolutionItem: null,
  autoLogoutTimeGap,
}

const VideoStateSlice = createSlice({
  name: "videoState",
  initialState,
  reducers: {
    // 更新全屏状态
    updateFullScreen: (state, action: PayloadAction<boolean>) => {
      state.isFullScreen = action.payload
    },
    // 更新退出弹窗弹出状态
    setIsOpenExitModal: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload
    },

    // 设置是否使用  mac鼠标滚轮方向
    setIsUseMacMouseWheelDir: (state, action: PayloadAction<boolean>) => {
      state.isUseMacMouseWheelDir = action.payload
      AppPreferenceCache.setAppPreference({
        isUseMacMouseWheelDir: state.isUseMacMouseWheelDir,
      })
    },
    setMouseScrollSpeed: (state, action: PayloadAction<number>) => {
      state.mouseScrollSpeed = Math.max(0, Math.min(1, action.payload))
      AppPreferenceCache.setAppPreference({
        mouseScrollSpeed: state.mouseScrollSpeed,
      })
    },
    // 设置是否使用  mac鼠标滚轮方向
    setIsUseCommonShortcut: (state, action: PayloadAction<boolean>) => {
      state.isUseCommonShortcut = action.payload
      AppPreferenceCache.setAppPreference({
        isUseCommonShortcut: state.isUseCommonShortcut,
      })
    },

    setIsGameModel: (state, action: PayloadAction<boolean>) => {
      state.isGameModel = action.payload
      AppPreferenceCache.setAppPreference({
        isGameModel: state.isGameModel,
      })
    },

    setIsThreeDMouse: (state, action: PayloadAction<boolean>) => {
      state.isThreeDMouse = action.payload
    },

    setResolutionList: (state, action: PayloadAction<AppSelectOption[]>) => {
      state.resolutionList = action.payload
    },

    setResolutionItem: (state, action: PayloadAction<Resolution>) => {
      state.resolutionItem = action.payload
      AppPreferenceCache.setAppPreference({
        resolutionItem: state.resolutionItem,
      })
    },

    setAgentResolutionItem: (
      state,
      action: PayloadAction<AppSelectOption | null>
    ) => {
      state.agentResolutionItem = action.payload
    },
    setAutoLogoutTimeGap: (state, action: PayloadAction<AutoLogoutTimeGap>) => {
      state.autoLogoutTimeGap = action.payload
      AppPreferenceCache.setAppPreference({
        autoLogoutTimeGap: state.autoLogoutTimeGap,
      })
    },
  },
})

export const {
  updateFullScreen,
  setIsOpenExitModal,
  setIsUseMacMouseWheelDir,
  setMouseScrollSpeed,
  setIsUseCommonShortcut,
  setIsGameModel,
  setIsThreeDMouse,
  setResolutionList,
  setResolutionItem,
  setAgentResolutionItem,
  setAutoLogoutTimeGap,
} = VideoStateSlice.actions

export const selectIsFullScreen = (state: RootState) =>
  state.remoteDeskState.isFullScreen

export const selectIsOpenExitModal = (state: RootState) =>
  state.remoteDeskState.isOpen

export const selectIsUseMacMouseWheelDir = (state: RootState) =>
  state.remoteDeskState.isUseMacMouseWheelDir

export const selectMouseScrollSpeed = (state: RootState) =>
  state.remoteDeskState.mouseScrollSpeed

export const selectIsUseMacCommonShortcut = (state: RootState) =>
  state.remoteDeskState.isUseCommonShortcut

export const selectIsGameModel = (state: RootState) =>
  state.remoteDeskState.isGameModel

export const selectIsThreeDMouse = (state: RootState) =>
  state.remoteDeskState.isThreeDMouse

export const selectResolutionList = (state: RootState) =>
  state.remoteDeskState.resolutionList

export const selectResolutionItem = (state: RootState) =>
  state.remoteDeskState.resolutionItem

export const selectAgentResolutionItem = (state: RootState) =>
  state.remoteDeskState.agentResolutionItem

export const selectAutoLogoutTimeGap = (state: RootState) =>
  state.remoteDeskState.autoLogoutTimeGap

export default VideoStateSlice.reducer
